import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { PowerOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { FEATURE_LABELS, type FinanceFeature } from '@/lib/finance'
import { useFinanceFeatures } from '@/hooks/useFinanceFeatures'

/**
 * Tela de parte desligada do financeiro (feature flag do servidor): mostra o
 * aviso no lugar da página. O código e os dados continuam lá.
 */
export function FinanceFeatureGate({ feature, children }: { feature: FinanceFeature; children: ReactNode }) {
  const { features, loading } = useFinanceFeatures()
  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando…</span>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-40" />
      </div>
    )
  }
  if (features[feature]) return <>{children}</>
  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-4 p-4 sm:p-6">
      <Card>
        <CardContent className="flex flex-col items-center gap-3 text-center">
          <PowerOff className="size-8 text-muted-foreground" aria-hidden="true" />
          <h1 className="text-lg font-semibold">{FEATURE_LABELS[feature]}: recurso desativado</h1>
          <p className="break-words text-sm text-muted-foreground">
            Esta parte do financeiro está desligada nesta instalação. Os dados antigos foram preservados;
            para religar, um administrador inclui <code>{feature}</code> em <code>FINANCE_FEATURES</code> na API.
          </p>
          <Button variant="link" asChild>
            <Link to="/finance-v2/transactions">Ir para os lançamentos</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
